import type { CatalogItemGroupEntity } from '@peritasai/repository';
import type { RoomAsset } from '../hooks/useAssetDetectionStatus';

/** One required item on the case build checklist and the room asset satisfying it. */
export interface ChecklistSlot {
  checklistItemId: string;
  name: string;
  detectedAsset: RoomAsset | null;
  /** Asset actively assigned to this slot on the case, whether or not it is in the room. */
  assignedAssetId: string | null;
  /** True when this slot's asset was reassigned to another case, so it needs re-verifying. */
  wasReassigned: boolean;
  /** True when detectedAsset is not yet allocated to this slot on the case (empty slot, or reserved but not allocated). */
  needsAssignment: boolean;
}

/**
 * Every asset actively assigned to one of the given groups' checklist items.
 * These can only satisfy their own slot, never fill an empty one.
 */
export function getReservedAssetIds(
  groups: CatalogItemGroupEntity[]
): Set<string> {
  const reserved = new Set<string>();
  groups.forEach((group) => {
    group.items.forEach((item) => {
      if (item.isActivelyAssigned && item.assetId) reserved.add(item.assetId);
    });
  });
  return reserved;
}

/**
 * Matches room assets to checklist slots. A slot with an actively assigned
 * asset is detected only when that exact asset is in the room; an empty slot
 * is filled by any unreserved room asset of the same catalog item.
 */
export function allocateChecklistSlots(
  groups: CatalogItemGroupEntity[],
  assetsInRoom: RoomAsset[],
  reservedAssetIds: Set<string>
): ChecklistSlot[] {
  const roomAssetById = new Map(assetsInRoom.map((asset) => [asset.id, asset]));
  const used = new Set<string>();

  return groups.flatMap((group) => {
    const pool = assetsInRoom.filter(
      (asset) =>
        asset.assetCatalogId === group.catalogItemId &&
        !reservedAssetIds.has(asset.id)
    );

    return group.items.map((item): ChecklistSlot => {
      if (item.isActivelyAssigned && item.assetId) {
        const detectedAsset = roomAssetById.get(item.assetId) ?? null;
        return {
          checklistItemId: item.checklistItemId,
          name: group.name,
          detectedAsset,
          assignedAssetId: item.assetId,
          wasReassigned: false,
          // Reserved in Build Cart (assetId set, not yet allocated) — still
          // needs assign-batch to promote it from RESERVED to ALLOCATED.
          needsAssignment: !item.isAllocated && detectedAsset !== null,
        };
      }

      const candidate = pool.find((asset) => !used.has(asset.id)) ?? null;
      if (candidate) used.add(candidate.id);
      return {
        checklistItemId: item.checklistItemId,
        name: group.name,
        detectedAsset: candidate,
        assignedAssetId: null,
        wasReassigned: item.reassigned,
        needsAssignment: candidate !== null,
      };
    });
  });
}
